import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Branch, BranchSyncStatusUpdate, BranchMenuSyncStatusUpdate, BranchPosConfig } from '../../types';
import { apiService } from '../../services/apiService';
import { showSuccessMessage, showErrorMessage } from './uiSlice';
import type { RootState } from '../index';

interface BranchError {
  status?: number;
  message: string;
  details?: any;
}

interface BranchState {
  branches: Branch[];
  selectedBranch: Branch | null;
  loading: boolean;
  error: string | BranchError | null;
  syncingBranches: string[]; // Track which branches are currently syncing
}

const initialState: BranchState = {
  branches: [],
  selectedBranch: null,
  loading: false,
  error: null,
  syncingBranches: [],
};

// Helper function to handle error payloads consistently
const handleErrorPayload = (payload: any): string | BranchError => {
  if (typeof payload === 'object' && payload !== null) {
    return {
      status: payload.status,
      message: payload.message || 'An error occurred',
      details: payload.details
    };
  }
  return payload || 'An unknown error occurred';
};

// Async thunks
export const fetchAllBranches = createAsyncThunk(
  'branches/fetchAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const branches = await apiService.getBranches();
      // Removed success notification for load operations to reduce noise
      return branches;
    } catch (error: any) {
      const errorInfo = {
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch branches',
        details: error.response?.data || error.message || 'Unknown error occurred'
      };
      dispatch(showErrorMessage(errorInfo.message));
      return rejectWithValue(errorInfo);
    }
  }
);

export const fetchBranchById = createAsyncThunk(
  'branches/fetchById',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      const branch = await apiService.getBranchById(id);
      // Removed success notification for load operations to reduce noise
      return branch;
    } catch (error: any) {
      const errorInfo = {
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || 'Failed to fetch branch by ID',
        details: error.response?.data || error.message || 'Unknown error occurred'
      };
      dispatch(showErrorMessage(errorInfo.message));
      return rejectWithValue(errorInfo);
    }
  }
);

export const updateBranchSyncStatus = createAsyncThunk(
  'branches/updateSyncStatus',
  async ({ id, isSyncEnabled }: { id: string; isSyncEnabled: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const syncStatusUpdate: BranchSyncStatusUpdate = { isSyncEnabled };
      const branch = await apiService.updateBranchSyncStatus(id, syncStatusUpdate);
      const statusText = isSyncEnabled ? 'enabled' : 'disabled';
      const branchName = branch.name || `Branch ${id}`;
      dispatch(showSuccessMessage(`Successfully ${statusText} sync for branch: ${branchName}`));
      return branch;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      dispatch(showErrorMessage(`Failed to update branch sync status: ${errorMessage}`));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBranchActiveStatus = createAsyncThunk(
  'branches/updateActiveStatus',
  async ({ id, isActive }: { id: string; isActive: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const branch = await apiService.updateBranch(id, { isActive });
      const statusText = isActive ? 'activated' : 'deactivated';
      const branchName = branch.name || `Branch ${id}`;
      dispatch(showSuccessMessage(`Successfully ${statusText} branch: ${branchName}`));
      return branch;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update branch active status';
      dispatch(showErrorMessage(`Failed to update branch status: ${errorMessage}`));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBranchMenuSyncStatus = createAsyncThunk(
  'branches/updateMenuSyncStatus',
  async ({ id, isMenuSynced }: { id: string; isMenuSynced: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const menuSyncStatusUpdate: BranchMenuSyncStatusUpdate = { isMenuSynced };
      const branch = await apiService.updateBranchMenuSyncStatus(id, menuSyncStatusUpdate);
      const statusText = isMenuSynced ? 'enabled' : 'disabled';
      const branchName = branch.name || `Branch ${id}`;
      dispatch(showSuccessMessage(`Successfully ${statusText} menu sync for branch: ${branchName}`));
      return branch;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      dispatch(showErrorMessage(`Failed to update menu sync status: ${errorMessage}`));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBranchPosConfig = createAsyncThunk(
  'branches/updatePosConfig',
  async ({ id, config }: { id: string; config: BranchPosConfig }, { rejectWithValue, dispatch }) => {
    try {
      const branch = await apiService.updateBranchPosConfig(id, config);
      const branchName = branch.name || `Branch ${id}`;
      dispatch(showSuccessMessage(`Successfully updated POS configuration for branch: ${branchName}`));
      return branch;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      dispatch(showErrorMessage(`Failed to update POS configuration: ${errorMessage}`));
      return rejectWithValue(errorMessage);
    }
  }
);

export const syncBranchWithTalabat = createAsyncThunk(
  'branches/syncWithTalabat',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      // Use the sync all branches method instead since there's no single branch sync
      const result = await apiService.syncBranchesWithTalabat();
      // Find the updated branch in the result
      const updatedBranch = result.branches?.find(b => b.id === id);
      if (!updatedBranch) {
        throw new Error(`Branch ${id} not found in sync result`);
      }
      const branchName = updatedBranch.name || `Branch ${id}`;
      dispatch(showSuccessMessage(`Successfully synced branch: ${branchName} with Talabat`));
      return updatedBranch;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      dispatch(showErrorMessage(`Failed to sync branch with Talabat: ${errorMessage}`));
      return rejectWithValue(errorMessage);
    }
  }
);

export const syncBranchMenu = createAsyncThunk(
  'branches/syncMenu',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      await apiService.syncBranchMenuItems(id);
      // Refetch the branch to get updated data, as syncBranchMenuItems API doesn't return the branch
      const updatedBranch = await apiService.getBranchById(id);
      const branchName = updatedBranch.name || `Branch ${id}`;
      dispatch(showSuccessMessage(`Successfully synced menu items for branch: ${branchName}`));
      return updatedBranch;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      dispatch(showErrorMessage(`Failed to sync branch menu: ${errorMessage}`));
      return rejectWithValue(errorMessage);
    }
  }
);

export const syncAllBranchesDetails = createAsyncThunk(
  'branches/syncAllDetails',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.syncBranchesWithTalabat();
      dispatch(showSuccessMessage(`Successfully synced ${response.syncedCount} branches with Talabat`));
      return response; // Return the full SyncAllBranchesResult
    } catch (error: any) {
      const errorInfo = {
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || 'Failed to sync all branches',
        details: error.response?.data || error.message || 'Unknown error occurred'
      };
      dispatch(showErrorMessage(errorInfo.message));
      return rejectWithValue(errorInfo);
    }
  }
);

export const syncAllBranchItemsThunk = createAsyncThunk(
  'branches/syncAllBranchItems',
  async (id: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await apiService.syncAllBranchItems(id);
      
      // Get branch name from current state
      const state = getState() as RootState;
      const branch = state.branches.branches.find((b: Branch) => b.id === id);
      const branchName = branch?.name || `Branch ${id}`;
      
      dispatch(showSuccessMessage(`Successfully synced ${response.totalSyncedCount} items for branch: ${branchName}`));
      return response;
    } catch (error: any) {
      // Get branch name for error message too
      const state = getState() as RootState;
      const branch = state.branches.branches.find((b: Branch) => b.id === id);
      const branchName = branch?.name || `Branch ${id}`;
      
      // Capture detailed error information
      const errorInfo = {
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || `Failed to sync all items for branch: ${branchName}`,
        details: error.response?.data || error.message || 'Unknown error occurred'
      };
      dispatch(showErrorMessage(errorInfo.message));
      return rejectWithValue(errorInfo);
    }
  }
);

const branchSlice = createSlice({
  name: 'branches',
  initialState,
  reducers: {
    setSelectedBranch: (state, action: PayloadAction<Branch | null>) => {
      state.selectedBranch = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    addSyncingBranch: (state, action: PayloadAction<string>) => {
      if (!state.syncingBranches.includes(action.payload)) {
        state.syncingBranches.push(action.payload);
      }
    },
    removeSyncingBranch: (state, action: PayloadAction<string>) => {
      state.syncingBranches = state.syncingBranches.filter(id => id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all branches
      .addCase(fetchAllBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload;
      })
      .addCase(fetchAllBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = handleErrorPayload(action.payload);
      })
      
      // Fetch branch by ID
      .addCase(fetchBranchById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranchById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBranch = action.payload;
        // Update the branch in the array if it exists
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
      })      .addCase(fetchBranchById.rejected, (state, action) => {
        state.loading = false;
        state.error = handleErrorPayload(action.payload);
      })
      
      // Update sync status
      .addCase(updateBranchSyncStatus.fulfilled, (state, action) => {
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
      })
      // Update active status
      .addCase(updateBranchActiveStatus.fulfilled, (state, action) => {
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
      })
      
      // Update menu sync status
      .addCase(updateBranchMenuSyncStatus.fulfilled, (state, action) => {
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
      })
      
      // Update POS config
      .addCase(updateBranchPosConfig.fulfilled, (state, action) => {
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
      })
      
      // Sync with Talabat
      .addCase(syncBranchWithTalabat.pending, (state, action) => {
        if (!state.syncingBranches.includes(action.meta.arg)) {
          state.syncingBranches.push(action.meta.arg);
        }
      })
      .addCase(syncBranchWithTalabat.fulfilled, (state, action) => {
        state.syncingBranches = state.syncingBranches.filter(id => id !== action.payload.id);
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
      })
      .addCase(syncBranchWithTalabat.rejected, (state, action) => {
        state.syncingBranches = state.syncingBranches.filter(id => id !== action.meta.arg);
        state.error = action.payload as string;
      })
      
      // Sync menu
      .addCase(syncBranchMenu.pending, (state, action) => {
        if (!state.syncingBranches.includes(action.meta.arg)) {
          state.syncingBranches.push(action.meta.arg);
        }
      })
      .addCase(syncBranchMenu.fulfilled, (state, action) => {
        state.syncingBranches = state.syncingBranches.filter(id => id !== action.payload.id);
        const index = state.branches.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
      })
      .addCase(syncBranchMenu.rejected, (state, action) => {
        state.syncingBranches = state.syncingBranches.filter(id => id !== action.meta.arg);
        state.error = action.payload as string;
      })      // Sync all branches details
      .addCase(syncAllBranchesDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncAllBranchesDetails.fulfilled, (state, action) => {
        state.loading = false;
        // Update branches with the returned data from sync operation
        if (action.payload.branches && action.payload.branches.length > 0) {
          state.branches = action.payload.branches;
        }
      })
      .addCase(syncAllBranchesDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = handleErrorPayload(action.payload);
      })// Sync all branch items
      .addCase(syncAllBranchItemsThunk.pending, (state, action) => {
        if (!state.syncingBranches.includes(action.meta.arg)) {
          state.syncingBranches.push(action.meta.arg);
        }
      })
      .addCase(syncAllBranchItemsThunk.fulfilled, (state, action) => {
        // action.payload is now SyncAllResult, use branchId to identify the branch
        const branchId = action.payload.branchId;
        state.syncingBranches = state.syncingBranches.filter(id => id !== branchId);
        // Note: We don't update the branch in state since the thunk returns SyncAllResult, not Branch
        // The UI will use the SyncAllResult directly for displaying sync results
      })      .addCase(syncAllBranchItemsThunk.rejected, (state, action) => {
        state.syncingBranches = state.syncingBranches.filter(id => id !== action.meta.arg);
        state.error = handleErrorPayload(action.payload);
      });
  },
});

export const { setSelectedBranch, clearError, addSyncingBranch, removeSyncingBranch } = branchSlice.actions;
export default branchSlice.reducer;
